import React, { useEffect, useState, useRef } from 'react'
import { FaPlay, FaPause } from 'react-icons/fa'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import './AudioPlayer.css'
import { AudioPlayerStyle } from './styled'

const activeAudioElements = new Set()

const AudioPlayer = ({
  autoPlay = false,
  src,
  isCircle = true,
  isCourse = false
}) => {
  const audioRef = useRef(null)
  const progressRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  //const [duration, setDuration] = useState(0);
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    const audioElement = audioRef.current
    if (!audioElement) return

    const updateProgress = () => {
      const currentTime = audioElement.currentTime
      const audioDuration = audioElement.duration
      if (isFinite(audioDuration) && audioDuration > 0) {
        setProgress((currentTime / audioDuration) * 100)
      }
    }

    const handleLoadedMetadata = () => {
      //setDuration(audioElement.duration);
      setIsReady(true)
    }

    const handleAudioEnd = () => {
      setPlaying(false)
      setProgress(0)
      audioElement.currentTime = 0
    }

    audioElement.addEventListener('timeupdate', updateProgress)
    audioElement.addEventListener('ended', handleAudioEnd)
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata)
    audioElement.addEventListener('canplaythrough', handleLoadedMetadata)

    return () => {
      audioElement.removeEventListener('timeupdate', updateProgress)
      audioElement.removeEventListener('ended', handleAudioEnd)
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audioElement.removeEventListener('canplaythrough', handleLoadedMetadata)
    }
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !src || !isReady) return

    const playAudio = async () => {
      try {
        if (autoPlay) {
          const playPromise = audio.play()
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setPlaying(true)
              })
              .catch(error => {
                console.warn('AutoPlay failed:', error)
                setPlaying(false)
              })
          }
        }
      } catch (error) {
        console.warn('AutoPlay error:', error)
        setPlaying(false)
      }
    }

    audio.load()
    activeAudioElements.add(audio)
    playAudio()

    return () => {
      audio.pause()
      audio.currentTime = 0
      activeAudioElements.delete(audio)
    }
  }, [src, autoPlay, isReady])

  const togglePlayPause = e => {
    e.stopPropagation()
    const audioElement = audioRef.current
    if (!audioElement) return

    if (audioElement.paused) {
      const playPromise = audioElement.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setPlaying(true)
          })
          .catch(error => {
            console.warn('Play failed:', error)
            setPlaying(false)
          })
      }
    } else {
      audioElement.pause()
      setPlaying(false)
    }
  }

  const handleSeek = event => {
    event.preventDefault()
    const audioElement = audioRef.current
    if (!audioElement || !isFinite(audioElement.duration)) return

    const rect = progressRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    let angle = Math.atan2(event.clientY - centerY, event.clientX - centerX)

    let degrees = (angle * (180 / Math.PI) + 90) % 360
    if (degrees < 0) degrees += 360

    let percentage = (degrees / 360) * 100

    const newTime = (percentage / 100) * audioElement.duration

    if (isFinite(newTime)) {
      audioElement.currentTime = newTime
      setProgress(percentage)
    }
  }
  if (isCircle) {
    return (
      <div className="audio-player-container">
        <audio ref={audioRef} src={src} preload="auto" />
        <div className="progress-circle" onClick={handleSeek} ref={progressRef}>
          <div className="circular-progressbar-container">
            <CircularProgressbar
              value={progress}
              styles={buildStyles({
                pathColor: '#4caf50',
                trailColor: '#ddd',
                strokeLinecap: 'round',
                transform: 'rotate(-90deg)',
                transformOrigin: 'center center'
              })}
            />
          </div>
          <button
            className="play-pause-btn"
            onMouseDown={e => e.stopPropagation()}
            onClick={togglePlayPause}
          >
            {playing ? <FaPause /> : <FaPlay />}
          </button>
        </div>
      </div>
    )
  }

  return (
    <AudioPlayerStyle height={isCourse ? '100%' : '40px'}>
      <audio
        ref={audioRef}
        controls
        autoPlay={autoPlay}
        controlsList="nodownload"
        preload="none"
      >
        <source src={src} type="audio/mp3" />
        Your browser does not support the audio tag.
      </audio>
    </AudioPlayerStyle>
  )
}

export default AudioPlayer
